import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';

export interface DailyTrendPoint {
  date: string;
  issues: number;
  turnIns: number;
  exchanges: number;
  customers: number;
  totalValue: number;
  issuesValue: number;
  turnInsValue: number;
  exchangesValue: number;
  customersValue: number;
}

interface TransactionTrendAsset {
  lin?: string;
  nsn?: string;
  transQty?: number;
  actualPrice?: number;
  tax?: number;
  shipping?: number;
}

interface TransactionLogTrendEntry {
  transactionType: string;
  dateProcessed: string;
  memberId: string;
  assets?: TransactionTrendAsset[];
}

interface TransactionLogTrendResponse {
  transactions: TransactionLogTrendEntry[];
}

interface CatalogPriceEntity {
  entity_data?: {
    lin?: string;
    nsn?: string;
    unit_price?: number | string | null;
  };
}

const TREND_TRANSACTION_TYPES = ['ISSUE_OUT', 'TURN_IN', 'EXCHANGE'] as const;

const formatYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Fetches the last 7 days of transaction data and aggregates it into daily
 * trend points: issues, turn-ins, exchanges, and unique customer counts.
 *
 * @param enabled - When false the query is skipped (used for lazy modal loading).
 */
export const useDashboardTrend = (enabled = true) => {
  return useQuery({
    queryKey: ['dashboard-trend'],
    enabled,
    queryFn: async (): Promise<DailyTrendPoint[]> => {
      const now = new Date();
      const fromDate = new Date(now);
      fromDate.setDate(now.getDate() - 6);
      fromDate.setHours(0, 0, 0, 0);

      const params = new URLSearchParams({
        from: formatYmd(fromDate),
        to: formatYmd(now),
        types: TREND_TRANSACTION_TYPES.join(','),
        // Large enough to avoid trimming recent, relevant trend transactions.
        limit: '2000',
      });

      const response = await httpClient.get<TransactionLogTrendResponse>(
        `/api/v1/transactions/log?${params.toString()}`,
      );
      const records = Array.isArray(response?.transactions) ? response.transactions : [];

      const catalogRows = await httpClient.get<CatalogPriceEntity[]>(
        '/api/v1/entities?entity_type=eq.catalog&is_current=eq.true&select=entity_data&limit=5000',
      );

      const unitPriceByLin = new Map<string, number>();
      const unitPriceByNsn = new Map<string, number>();
      for (const row of catalogRows ?? []) {
        const data = row?.entity_data;
        if (!data) continue;

        const unitPriceRaw = data.unit_price;
        const unitPrice =
          typeof unitPriceRaw === 'number'
            ? unitPriceRaw
            : typeof unitPriceRaw === 'string'
              ? Number.parseFloat(unitPriceRaw)
              : NaN;

        if (!Number.isFinite(unitPrice)) continue;

        const lin = data.lin?.trim().toUpperCase();
        const nsn = data.nsn?.trim().toUpperCase();
        if (lin) unitPriceByLin.set(lin, unitPrice);
        if (nsn) unitPriceByNsn.set(nsn, unitPrice);
      }

      // Build one slot per calendar day for the past 7 days (today included).
      const slots = new Map<
        string,
        {
          date: string;
          issues: number;
          turnIns: number;
          exchanges: number;
          customerIds: Set<string>;
          totalValue: number;
          issuesValue: number;
          turnInsValue: number;
          exchangesValue: number;
          customersValue: number;
        }
      >();

      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(now.getDate() - i);
        const key = `${d.getMonth() + 1}/${d.getDate()}`;
        slots.set(key, {
          date: key,
          issues: 0,
          turnIns: 0,
          exchanges: 0,
          customerIds: new Set(),
          totalValue: 0,
          issuesValue: 0,
          turnInsValue: 0,
          exchangesValue: 0,
          customersValue: 0,
        });
      }

      // Cutoff = start of the day 6 days ago.
      const cutoff = fromDate;

      for (const record of records) {
        if (!record.dateProcessed) continue;
        const d = new Date(record.dateProcessed);
        if (d < cutoff || d > now) continue;

        const key = `${d.getMonth() + 1}/${d.getDate()}`;
        const slot = slots.get(key);
        if (!slot) continue;

        if (record.transactionType === 'ISSUE_OUT') slot.issues++;
        else if (record.transactionType === 'TURN_IN') slot.turnIns++;
        else if (record.transactionType === 'EXCHANGE') slot.exchanges++;

        const assets = Array.isArray(record.assets) ? record.assets : [];
        for (const asset of assets) {
          const quantity =
            typeof asset.transQty === 'number' && Number.isFinite(asset.transQty)
              ? asset.transQty
              : 0;
          if (quantity <= 0) continue;

          const directUnitPrice =
            typeof asset.actualPrice === 'number' && Number.isFinite(asset.actualPrice)
              ? asset.actualPrice + (asset.tax ?? 0) + (asset.shipping ?? 0)
              : null;

          const linKey = asset.lin?.trim().toUpperCase() ?? '';
          const nsnKey = asset.nsn?.trim().toUpperCase() ?? '';

          const catalogUnitPrice =
            (linKey ? unitPriceByLin.get(linKey) : undefined) ??
            (nsnKey ? unitPriceByNsn.get(nsnKey) : undefined) ??
            0;

          const unitPrice = directUnitPrice ?? catalogUnitPrice;
          if (!Number.isFinite(unitPrice) || unitPrice <= 0) continue;

          const lineTotal = quantity * unitPrice;
          slot.totalValue += lineTotal;
          if (record.transactionType === 'ISSUE_OUT') slot.issuesValue += lineTotal;
          else if (record.transactionType === 'TURN_IN') slot.turnInsValue += lineTotal;
          else if (record.transactionType === 'EXCHANGE') slot.exchangesValue += lineTotal;
        }

        if (record.memberId) slot.customerIds.add(record.memberId);
      }

      return Array.from(slots.values()).map(({ customerIds, ...rest }) => ({
        ...rest,
        customers: customerIds.size,
        totalValue: Math.round(rest.totalValue * 100) / 100,
        issuesValue: Math.round(rest.issuesValue * 100) / 100,
        turnInsValue: Math.round(rest.turnInsValue * 100) / 100,
        exchangesValue: Math.round(rest.exchangesValue * 100) / 100,
        customersValue: Math.round(rest.customersValue * 100) / 100,
      }));
    },
    // Always fetch fresh data when the modal opens so that trend points
    // reflect the current date window (day roll-over) and any seed resets.
    staleTime: 0,
  });
};
